'use client'

import { useState, useRef, useEffect } from 'react'
import clsx from 'clsx'
import SubBannerModal from './SubBannerModal'
import { FaSearch } from 'react-icons/fa'
import { RxCross1 } from 'react-icons/rx'
import { useGetUserSearchedQuery } from '../../services/apiSlice'
import { UserSimple } from '../../services/schema/User'

export interface ComponentDimensionProps {
  x: number
  y: number
  height: number
  width: number
}

export default function SubBannerHeader() {
  const inputRef = useRef<HTMLDivElement>(null)
  const [searchInput, setSearchInput] = useState<string>('')
  const [filteredUsers, setFilteredUsers] = useState<UserSimple[]>([])

  const [inputIsFocused, setInputIsFocused] = useState<boolean>(false)
  const [modalIsFocused, setModalIsFocused] = useState<boolean>(false)

  const { data, isSuccess } = useGetUserSearchedQuery(null, {
    refetchOnMountOrArgChange: false,
  })

  useEffect(() => {
    const inputRefCurrent = inputRef.current
    if (inputRefCurrent) {
      const handleClickOutsideInput = (event: MouseEvent) => {
        const target = event.target as Node

        const clickedOutsideInput =
          inputRefCurrent && !inputRefCurrent.contains(target)

        if (clickedOutsideInput) {
          setInputIsFocused(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutsideInput)
      return () => {
        document.removeEventListener('mousedown', handleClickOutsideInput)
      }
    }
  }, [inputRef])

  if (data && isSuccess) {
    const users = data.users
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputQuery = event.target.value
      setSearchInput(inputQuery)

      let filteredData = users.filter((user) => {
        if (
          inputQuery === '' ||
          !user.username.toLowerCase().includes(inputQuery.toLowerCase())
        ) {
          return
        } else {
          return user
        }
      })

      if (filteredData.length > 10) {
        filteredData.splice(10)
        setFilteredUsers(filteredData)
      } else if (filteredData.length === 0 || inputQuery === '') {
        setFilteredUsers([])
      } else {
        setFilteredUsers(filteredData)
      }
    }

    return (
      <section className="sb-header">
        <form
          className={clsx(
            'relative flex h-full grow items-center rounded-full',
            {
              'border-blue bgd-theme border border-solid': inputIsFocused,
              'border-theme bgd-theme border border-solid': !inputIsFocused,
            },
          )}
        >
          <div className="flex h-full w-[35px] items-center justify-end">
            <FaSearch
              className={clsx('', {
                'text-sub': !inputIsFocused,
                'text-blue': inputIsFocused,
              })}
            />
          </div>
          <div
            ref={inputRef}
            className="relative flex h-full w-[265px] grow"
            onFocus={() => {
              setInputIsFocused(true)
              setModalIsFocused(true)
            }}
          >
            <input
              className={clsx(
                'flex h-full grow rounded-full border-none bg-inherit pl-[15px] text-sm outline-none focus:ring-0 focus:outline-none',
                {
                  'pr-[20px]': !inputIsFocused,
                  'pr-[40px]': inputIsFocused,
                },
              )}
              placeholder="Search"
              type="text"
              value={searchInput}
              onChange={(event) => {
                handleInputChange(event)
              }}
            />
            {inputIsFocused && searchInput.length > 0 && (
              <button
                className="bgd-blue absolute top-[50%] right-[10px] h-[20px] translate-y-[-50%] cursor-pointer rounded-full p-[3px] text-sm font-bold text-white"
                type="button"
                data-typeid="clear-search-btn"
                onClick={() => {
                  setSearchInput('')
                  setFilteredUsers([])
                }}
              >
                <RxCross1 />
              </button>
            )}
          </div>
        </form>
        {modalIsFocused && (
          <SubBannerModal
            usersArray={filteredUsers}
            searchInput={searchInput}
            inputRef={inputRef}
            setInputIsFocused={setInputIsFocused}
            setModalIsFocused={setModalIsFocused}
          />
        )}
      </section>
    )
  }
}
